/**
 * Stripe Connect OAuth helpers (Standard accounts).
 *
 * The dashboard builds the authorize URL, records a one-time `state` nonce
 * under the coach org, and, when Stripe sends the coach back to /sell/setup
 * with `code` and `state`, hands both to the `exchangeStripeConnect` callable.
 * The function verifies the nonce, exchanges the code with the platform secret
 * key, and writes `stripeAccountId` and `stripePubKey` into `productInfo`.
 * A connected org can later be unlinked through the `disconnectStripeConnect`
 * callable. `docs/stripe-connect.md` holds both contracts; the functions live
 * in the firebase-cloud repo.
 */

export const STRIPE_CONNECT_AUTHORIZE_URL = 'https://connect.stripe.com/oauth/authorize'

// Stripe sends the coach back to this path on the dashboard. Every origin the
// dashboard runs on has to be registered, with this path, in the Stripe
// account (or sandbox) that owns the client ID.
export const STRIPE_CONNECT_RETURN_PATH = '/sell/setup'

// The callables that finish a connection and undo one. Both are deployed in
// the project's default Cloud Functions region.
export const STRIPE_CONNECT_EXCHANGE_FUNCTION = 'exchangeStripeConnect'
export const STRIPE_CONNECT_DISCONNECT_FUNCTION = 'disconnectStripeConnect'

// Where the one-time nonce waits between the Connect click and Stripe sending
// the coach back. It sits directly under the org, not under `productInfo`,
// because `productInfo` is readable by anyone (the checkout site needs it) and
// database rules cannot take back a read granted at a parent. At the org level
// only the org's admins can read or write it, which is what keeps the nonce a
// secret. The callable reads the same path.
export function getStripeConnectPendingStatePath(coachOrgId) {
  return `coachOrgs/${coachOrgId}/stripeConnect/pendingState`
}

// How long a pending state stays valid. The function rejects anything older,
// so a stale nonce left behind by an abandoned attempt can never be replayed.
export const STRIPE_CONNECT_STATE_TTL_MS = 10 * 60 * 1000

// Separates the org id from the nonce inside `state`. Firebase push keys and
// hex nonces never contain it, so the function can split on the first one.
export const STRIPE_CONNECT_STATE_SEPARATOR = ':'

// Everything Stripe may append to the return URL. The page strips all of them
// before doing anything else, so a refresh or back-navigation cannot replay
// the code.
export const STRIPE_CONNECT_RETURN_PARAMS = ['code', 'scope', 'state', 'error', 'error_description']

// `reason` values the exchange callable reports in `HttpsError.details`, plus
// Stripe's own `error` value when the coach backs out on stripe.com.
export const STRIPE_CONNECT_ERROR_MESSAGES = {
  access_denied: 'The Stripe connection was cancelled before it finished. Nothing was changed.',
  invalid_state: 'This connection request expired or did not match your account. Please try again.',
  test_mode: 'That Stripe account is in test mode. Please connect a live Stripe account.',
  exchange_failed: 'Stripe did not accept the connection. Please try again.',
  // The org's products and prices live in the account it is already connected
  // to, so the function refuses to switch accounts until that one is disconnected.
  already_connected:
    'A different Stripe account is already connected. Disconnect it first, then connect the new account.',
}
export const STRIPE_CONNECT_DEFAULT_ERROR_MESSAGE = 'Connecting your Stripe account failed. Please try again.'

// `reason` values the disconnect callable reports.
export const STRIPE_DISCONNECT_ERROR_MESSAGES = {
  not_connected: 'There is no Stripe account connected to disconnect.',
  deauthorize_failed: 'Stripe could not disconnect the account right now. Please try again.',
}
export const STRIPE_DISCONNECT_DEFAULT_ERROR_MESSAGE = 'Disconnecting your Stripe account failed. Please try again.'

export function createStripeConnectNonce() {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined
  if (!cryptoApi?.getRandomValues) {
    throw new Error('Secure random values are not available in this browser')
  }
  const bytes = new Uint8Array(16)
  cryptoApi.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function buildStripeConnectState({ coachOrgId, nonce }) {
  return `${coachOrgId}${STRIPE_CONNECT_STATE_SEPARATOR}${nonce}`
}

export function parseStripeConnectState(state) {
  if (typeof state !== 'string') return null
  const separatorIdx = state.indexOf(STRIPE_CONNECT_STATE_SEPARATOR)
  if (separatorIdx <= 0 || separatorIdx === state.length - 1) return null
  return {
    coachOrgId: state.slice(0, separatorIdx),
    nonce: state.slice(separatorIdx + 1),
  }
}

// The dashboard sends Stripe back to itself, so each environment returns to
// the origin it ran on. There is no override: every origin has to be
// registered on Stripe's side either way.
export function getStripeConnectRedirectUri({ origin }) {
  return `${origin}${STRIPE_CONNECT_RETURN_PATH}`
}

export function buildStripeConnectUrl({ clientId, state, redirectUri, email }) {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    scope: 'read_write',
    state,
    redirect_uri: redirectUri,
  })
  if (email) {
    params.set('stripe_user[email]', email)
  }
  return `${STRIPE_CONNECT_AUTHORIZE_URL}?${params.toString()}`
}

export function getStripeConnectErrorMessage(reason) {
  return STRIPE_CONNECT_ERROR_MESSAGES[reason] || STRIPE_CONNECT_DEFAULT_ERROR_MESSAGE
}

export function getStripeDisconnectErrorMessage(reason) {
  return STRIPE_DISCONNECT_ERROR_MESSAGES[reason] || STRIPE_DISCONNECT_DEFAULT_ERROR_MESSAGE
}

// A callable failure arrives as an HttpsError; the function puts the reason
// it wants shown in `details.reason`. Anything else gets the generic message.
export function getStripeConnectExchangeErrorReason(error) {
  const reason = error?.details?.reason
  return typeof reason === 'string' ? reason : null
}
